import moment from 'moment';

import { BookingType } from '../../redux/types/bookingTypes';

export const MINUTES_IN_DAY = 24 * 60;

export type BookingTransitionKind = 'gap' | 'overlap';

export type BookingTransition = {
  kind: 'overlap';
  date: string;
  rentalObjectId: number;
  previousBookingId: number;
  nextBookingId: number;
  previousCheckoutTime: string;
  nextCheckinTime: string;
  startMinutes: number;
  endMinutes: number;
  durationMinutes: number;
};

export type BookingBarType = 'start' | 'ending';

export type BookingBarGeometry = {
  left: number;
  width: number;
  durationDays: number;
};

export type TransitionIndicatorGeometry = {
  left: number;
  width: number;
};

const DEFAULT_TIME_MINUTES = 12 * 60;

export const parseTimeToMinutes = (
  value: string | null | undefined,
): number | null => {
  if (!value) {
    return null;
  }

  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

const parseDate = (value: string): moment.Moment | null => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid() ? date.startOf('day') : null;
};

const compareDateTimes = (left: BookingType, right: BookingType): number => {
  const dateComparison = left.check_in_date.localeCompare(right.check_in_date);
  if (dateComparison !== 0) {
    return dateComparison;
  }

  const leftTime =
    parseTimeToMinutes(left.check_in_time) ?? DEFAULT_TIME_MINUTES;
  const rightTime =
    parseTimeToMinutes(right.check_in_time) ?? DEFAULT_TIME_MINUTES;
  return leftTime - rightTime || left.id - right.id;
};

export const getTransitionKind = (
  previous: BookingType,
  next: BookingType,
): BookingTransitionKind | null => {
  if (
    previous.rental_object !== next.rental_object ||
    previous.check_out_date !== next.check_in_date
  ) {
    return null;
  }

  const previousCheckout = parseTimeToMinutes(previous.check_out_time);
  const nextCheckin = parseTimeToMinutes(next.check_in_time);
  if (previousCheckout === null || nextCheckin === null) {
    return null;
  }

  if (nextCheckin >= previousCheckout) {
    return 'gap';
  }
  return 'overlap';
};

export const getBookingTransitions = (
  bookings: BookingType[],
): BookingTransition[] => {
  const bookingsByRentalObject = new Map<number, BookingType[]>();

  bookings.forEach((booking) => {
    const objectBookings =
      bookingsByRentalObject.get(booking.rental_object) || [];
    objectBookings.push(booking);
    bookingsByRentalObject.set(booking.rental_object, objectBookings);
  });

  const transitions: BookingTransition[] = [];
  bookingsByRentalObject.forEach((objectBookings) => {
    const sortedBookings = [...objectBookings].sort(compareDateTimes);

    sortedBookings.forEach((nextBooking, index) => {
      if (index === 0) {
        return;
      }

      const previousBooking = sortedBookings[index - 1];
      const kind = getTransitionKind(previousBooking, nextBooking);
      if (kind !== 'overlap') {
        return;
      }

      const previousCheckout = parseTimeToMinutes(
        previousBooking.check_out_time,
      );
      const nextCheckin = parseTimeToMinutes(nextBooking.check_in_time);
      if (previousCheckout === null || nextCheckin === null) {
        return;
      }

      transitions.push({
        kind,
        date: nextBooking.check_in_date,
        rentalObjectId: nextBooking.rental_object,
        previousBookingId: previousBooking.id,
        nextBookingId: nextBooking.id,
        previousCheckoutTime: previousBooking.check_out_time.slice(0, 5),
        nextCheckinTime: nextBooking.check_in_time.slice(0, 5),
        startMinutes: Math.min(previousCheckout, nextCheckin),
        endMinutes: Math.max(previousCheckout, nextCheckin),
        durationMinutes: Math.abs(previousCheckout - nextCheckin),
      });
    });
  });

  return transitions;
};

export const getBookingBarGeometry = (
  booking: BookingType,
  cellWidth: number,
  type: BookingBarType,
  currentDay: moment.Moment,
): BookingBarGeometry => {
  const checkInDate = parseDate(booking.check_in_date);
  const checkOutDate = parseDate(booking.check_out_date);
  const anchorDate =
    type === 'start' ? checkInDate : parseDate(currentDay.format('YYYY-MM-DD'));

  const checkInMinutes =
    parseTimeToMinutes(booking.check_in_time) ?? DEFAULT_TIME_MINUTES;
  const checkOutMinutes =
    parseTimeToMinutes(booking.check_out_time) ?? DEFAULT_TIME_MINUTES;

  if (!checkInDate || !checkOutDate || !anchorDate) {
    return {
      left: type === 'start' ? cellWidth / 2 + 1 : 1,
      width: Math.max(0, cellWidth - 2),
      durationDays: 0,
    };
  }

  const daysToCheckout = Math.max(0, checkOutDate.diff(anchorDate, 'days'));
  const startMinutes = type === 'start' ? checkInMinutes : 0;
  const endMinutes = daysToCheckout * MINUTES_IN_DAY + checkOutMinutes;
  const left =
    type === 'start' ? (cellWidth * checkInMinutes) / MINUTES_IN_DAY + 1 : 1;
  const width = Math.max(
    0,
    (cellWidth * (endMinutes - startMinutes)) / MINUTES_IN_DAY - 2,
  );

  return {
    left,
    width,
    durationDays: (endMinutes - startMinutes) / MINUTES_IN_DAY,
  };
};

export const getTransitionLabel = (transition: BookingTransition): string => {
  return `Наложение: выезд ${transition.previousCheckoutTime}, въезд ${transition.nextCheckinTime}`;
};

export const getTransitionIndicatorGeometry = (
  transition: BookingTransition,
  cellWidth: number,
): TransitionIndicatorGeometry => {
  const left = (cellWidth * transition.startMinutes) / MINUTES_IN_DAY;
  const width = Math.max(
    2,
    (cellWidth * (transition.endMinutes - transition.startMinutes)) /
      MINUTES_IN_DAY,
  );

  return { left, width };
};
